import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate, fmtINR, STATUS_STYLES } from "@/lib/format";
import { Search, ArrowUpDown, ChevronLeft, ChevronRight, Eye, Download, Pencil, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

export default function AdminPOs() {
  const [data, setData] = useState({ pos: [], total: 0, page: 1, page_size: 15 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState({ key: "updated_at", order: "desc" });
  const [page, setPage] = useState(1);
  const [editPo, setEditPo] = useState(null);
  const [confirmEdit, setConfirmEdit] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    const qs = new URLSearchParams({ page: String(page), page_size: "15", sort: sort.key, order: sort.order });
    if (q.trim()) qs.set("q", q.trim());
    if (status !== "all") qs.set("status", status);
    api.get(`/pos?${qs}`).then((r) => setData(r.data)).catch(() => toast.error("Failed to load POs"));
  }, [page, sort, status, q]);
  useEffect(() => { load(); }, [load]);

  const toggleSort = (key) => {
    setPage(1);
    setSort((s) => ({ key, order: s.key === key && s.order === "desc" ? "asc" : "desc" }));
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/pos/${editPo.id}`, {
        ...form,
        quantity: form.quantity !== "" ? Number(form.quantity) : undefined,
        value: form.value !== "" ? Number(form.value) : undefined,
      });
      toast.success("PO updated — change recorded in audit log");
      setConfirmEdit(false);
      setEditPo(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <div data-testid="admin-pos-page" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">PO Management</h1>
          <p className="text-sm text-muted-foreground">{data.total} POs across the organization · edits are audit-logged</p>
        </div>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pos-export`} target="_blank" rel="noreferrer">
          <Button variant="outline" data-testid="admin-export-btn"><Download className="mr-1.5 h-4 w-4" /> Export CSV</Button>
        </a>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3">
        <div className="relative min-w-64 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="admin-po-search" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search PO, supplier, item, tracking…" className="pl-9" />
        </div>
        <Select value={status} onValueChange={(v) => { setPage(1); setStatus(v); }}>
          <SelectTrigger data-testid="admin-po-status" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_STYLES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full text-sm" data-testid="admin-po-table">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              {[["po_number", "PO Number"], ["supplier_name", "Supplier"], [null, "Item"], ["value", "Value"], ["expected_delivery_date", "Expected"], ["status", "Status"], [null, "Buyer"], [null, "Actions"]].map(([key, label]) => (
                <th key={label} className="px-4 py-3 font-medium">
                  {key ? (
                    <button data-testid={`admin-sort-${key}`} onClick={() => toggleSort(key)} className="flex items-center gap-1 uppercase tracking-wide hover:text-slate-900">
                      {label} <ArrowUpDown className={`h-3 w-3 ${sort.key === key ? "text-slate-900" : "text-slate-300"}`} />
                    </button>
                  ) : label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.pos.map((p) => (
              <tr key={p.id} data-testid={`admin-po-row-${p.po_number}`} className="border-b last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono font-semibold">{p.po_number}</td>
                <td className="max-w-44 truncate px-4 py-3">{p.supplier_name}</td>
                <td className="max-w-44 truncate px-4 py-3 text-muted-foreground">{p.item}</td>
                <td className="px-4 py-3 text-right font-mono">{fmtINR(p.value)}</td>
                <td className="px-4 py-3">{fmtDate(p.expected_delivery_date)}</td>
                <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                <td className="px-4 py-3 text-muted-foreground">{p.buyer_name}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" data-testid={`admin-view-${p.po_number}`} onClick={() => navigate(`/admin/pos/${p.id}`)}><Eye className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`admin-edit-${p.po_number}`} onClick={() => {
                      setEditPo(p);
                      setForm({ item: p.item, quantity: p.quantity, value: p.value, required_delivery_date: p.required_delivery_date, expected_delivery_date: p.expected_delivery_date || "", delivery_location: p.delivery_location || "", notes: p.notes || "" });
                    }}><Pencil className="h-4 w-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between border-t px-4 py-2.5 text-sm">
          <p className="text-muted-foreground">Page {data.page} of {totalPages} · {data.total} POs</p>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" data-testid="admin-page-prev" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" data-testid="admin-page-next" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <Dialog open={!!editPo && !confirmEdit} onOpenChange={() => setEditPo(null)}>
        <DialogContent className="max-w-xl bg-white">
          <DialogHeader><DialogTitle className="font-heading">Edit {editPo?.po_number}</DialogTitle></DialogHeader>
          {editPo && (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Item</Label><Input data-testid="edit-po-item" className="mt-1.5" value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} /></div>
              <div><Label>Quantity</Label><Input data-testid="edit-po-qty" type="number" className="mt-1.5" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
              <div><Label>Value (₹)</Label><Input data-testid="edit-po-value" type="number" className="mt-1.5" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></div>
              <div><Label>Required Delivery</Label><Input data-testid="edit-po-req-date" type="date" className="mt-1.5" value={form.required_delivery_date} onChange={(e) => setForm({ ...form, required_delivery_date: e.target.value })} /></div>
              <div><Label>Expected Delivery</Label><Input data-testid="edit-po-exp-date" type="date" className="mt-1.5" value={form.expected_delivery_date} onChange={(e) => setForm({ ...form, expected_delivery_date: e.target.value })} /></div>
              <div className="col-span-2"><Label>Delivery Location</Label><Input data-testid="edit-po-location" className="mt-1.5" value={form.delivery_location} onChange={(e) => setForm({ ...form, delivery_location: e.target.value })} /></div>
              <DialogFooter className="col-span-2">
                <Button type="button" variant="outline" onClick={() => setEditPo(null)}>Cancel</Button>
                <Button data-testid="edit-po-review" onClick={() => setConfirmEdit(true)} className="bg-slate-900 hover:bg-slate-800">Review Changes</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmEdit} onOpenChange={() => setConfirmEdit(false)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Confirm PO Edit</DialogTitle>
            <DialogDescription>You are modifying critical PO data for {editPo?.po_number}. This change is recorded in the immutable audit log.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmEdit(false)}>Go Back</Button>
            <Button data-testid="edit-po-confirm" disabled={saving} onClick={saveEdit} className="bg-red-600 hover:bg-red-700">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
